import { isPlatformServer, AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	PLATFORM_ID,
	forwardRef,
} from '@angular/core';
import {
	DatalayerService,
	LocalStorageService,
	ModalEvent,
	ModalOverlayService,
	parseStringToIntOrDefault,
} from '@woolworthsnz/styleguide';
import { RecipeListRequest, RecipeSearchRequest } from '@woolworthsnz/trader-api';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMapTo, take } from 'rxjs/operators';
import { mapToStamps } from '../../helpers/stamp.helper';
import { RecipeService } from '../../services';
import { Stamp, StampType, RecipeStampLocal } from '../../models';
import { GridComponent } from '../grid/grid.component';

function isNonNull<T>(value: T): value is NonNullable<T> {
	return value != null;
}

export const placeholderRecipeStamp: RecipeStampLocal = {
	id: -1,
	title: '',
	prepDuration: null,
	cookDuration: null,
	numberOfIngredients: 0,
	mealTimes: [],
	servings: null,
	imageUrl: '',
	badge: null,
	saved: null,
};

export const ssrPlaceholders = 5;

@Component({
	selector: 'recipe-grid-container',
	template: `
		<recipe-grid
			[recipeStamps]="recipes"
			[savedRecipeIds]="savedRecipeIds$ | async"
			[mode]="mode"
			[initialServings]="initialServings"
			[stampType]="stampType"
			(saveRecipe)="saveRecipe($event)"
			(stampClick)="handleStampClick($event)"
			(addServing)="addServing.emit($event)"
			(removeServing)="removeServing.emit($event)"
			data-cy="recipe-grid"
		></recipe-grid>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [forwardRef(() => GridComponent), AsyncPipe],
})
export class GridContainerComponent implements OnInit, OnChanges {
	@Input() trackPromotionEvents = false;

	// Category inputs
	@Input() categoryId: number;
	@Input() sort: RecipeListRequest.SortEnum = 'Latest';
	@Input() pageSize = 5;
	@Input() page = 1;
	@Input() mode: 'grid' | 'carousel' = 'grid';

	// Search inputs
	@Input() search: string;
	@Input() target: RecipeSearchRequest.TargetEnum;

	// Directly passing recipe stamps
	@Input() recipes: Stamp[];
	@Input() stampType: StampType = StampType.recipe;

	// Meal Planning specific
	@Input() initialServings = 1;
	@Input() isMealPlanningContext = false;
	@Input() placeholders = ssrPlaceholders;
	@Output() addServing = new EventEmitter<{ stamp: Stamp; quantity: number }>();
	@Output() removeServing = new EventEmitter<{ stamp: Stamp; quantity: number }>();

	recipes$: Observable<Stamp[]>;
	loadSavedRecipes$ = new BehaviorSubject(true);
	savedRecipeIds$: Observable<number[]>;
	// Specific recipe inputs
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private _recipeIds: number[] = [];

	constructor(
		private recipeService: RecipeService,
		private modalOverlayService: ModalOverlayService,
		private datalayerService: DatalayerService,
		private cdr: ChangeDetectorRef,
		@Inject(PLATFORM_ID) private platformId: Object,
		private localStorageService: LocalStorageService
	) {}

	@Input() set recipeIds(value: number[] | string) {
		// When used as a custom element inputs are passed as strings so we assume it is a comma separated list of recipe IDs
		if (typeof value === 'string') {
			this._recipeIds = value
				.split(/[\s,]+/)
				.map((id) => parseStringToIntOrDefault(id, 0))
				.filter(Boolean);
		} else {
			this._recipeIds = value;
		}
	}

	ngOnInit(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.savedRecipeIds$ = this.loadSavedRecipes$.pipe(
			switchMapTo(
				this.recipeService
					.getSavedRecipes()
					.pipe(map((res) => res.savedRecipes?.map((r) => r.recipeId || 0) || []))
			)
		);
	}

	ngOnChanges(): void {
		// When rendered server side the api calls are expensive so we'll just render the stamps as placeholders and let the client load them late
		if (isPlatformServer(this.platformId)) {
			this.recipes = Array(this.placeholders).fill({ recipe: placeholderRecipeStamp });
			return;
		}

		// Category ID takes precedence over a list of recipe IDs
		if (this.categoryId) {
			this.recipes$ = this.recipeService
				.getRecipesForCategory(this.categoryId, { sort: this.sort, page: this.page, size: this.pageSize })
				.pipe(
					map((res) => mapToStamps(res.recipes || [])),
					catchError(() => EMPTY)
				);
		} else if (this._recipeIds.length) {
			this.recipes$ = forkJoin(
				this._recipeIds.map((id) => this.recipeService.getRecipeSummary(id).pipe(catchError(() => of(null))))
			).pipe(map((recipes) => mapToStamps(recipes.filter(isNonNull))));
		} else if (this.search) {
			const searchRequest: RecipeSearchRequest = {
				search: this.search,
				sort: this.sort,
				page: this.page,
				size: this.pageSize,
			};
			if (this.target) {
				searchRequest.target = this.target;
			}
			this.recipes$ = this.recipeService.searchRecipes(searchRequest).pipe(
				map((res) => mapToStamps(res.recipes || [])),
				catchError(() => EMPTY)
			);
		}

		this.recipes$?.pipe(take(1)).subscribe((recipes) => {
			this.recipes = recipes;
			this.trackRecipeImpressions(recipes);
			this.cdr.markForCheck();
		});
	}

	saveRecipe({ recipeId, isSaved }: { recipeId: number; isSaved: boolean }): void {
		const saveOrRemove = isSaved
			? this.recipeService.removeSavedRecipe(recipeId)
			: this.recipeService.saveRecipe(recipeId);

		saveOrRemove
			.pipe(
				take(1),
				catchError((err) => {
					if (err.status === 401) {
						this.modalOverlayService.open({
							eventType: ModalEvent.saveRecipe,
						});
					}
					return throwError(() => err);
				})
			)
			.subscribe(() => this.loadSavedRecipes$.next(true)); // reloads saved recipes
	}

	handleStampClick({ recipe, slot }: { recipe: Stamp; slot: number }): void {
		if (this.isMealPlanningContext) {
			this.localStorageService.setItem('isMealPlanningContext', String(true));
		}
		if (this.trackPromotionEvents) {
			this.datalayerService.trackPromoClick(this.mapRecipeToPromo(recipe.recipe, slot));
		}
	}

	private trackRecipeImpressions(recipes: Stamp[]): void {
		if (this.trackPromotionEvents) {
			this.datalayerService.trackPromoImpression(
				recipes.map(({ recipe }, index) => this.mapRecipeToPromo(recipe, index))
			);
		}
	}

	private mapRecipeToPromo(
		recipe: RecipeStampLocal,
		slot: number
	): { id: number | undefined; name: string | undefined; creative: string; position: string } {
		return {
			id: recipe.id,
			name: recipe.title,
			creative: 'Recipe ' + (this.mode === 'grid' ? 'Grid' : 'Carousel'),
			position: `Slot ${slot + 1}`,
		};
	}
}
